import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown, User, LogOut, LayoutDashboard } from "lucide-react";
import { useI18n } from "@/saas/i18n";
import { useSaasAuth } from "@/saas/context/SaasAuthContext";
import { BRAND } from "@/saas/config/brand";

/**
 * Glassmorphic sticky top navigation.
 * Responsive: desktop inline nav, mobile hamburger sheet.
 */
export default function Nav() {
  const { t, locale, setLocale, locales } = useI18n();
  const { user, logout } = useSaasAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const langRef = useRef(null);
  const acctRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setLangOpen(false); setAcctOpen(false); }, [loc.pathname]);

  useEffect(() => {
    const onDown = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const links = [
    { to: "/",        label: t("nav.home") },
    { to: "/plans",   label: t("nav.plans") },
    { to: "/app",     label: t("nav.app") },
  ];

  const activeLang = locales.find((l) => l.code === locale);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "bg-black/75 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-black/70 to-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center gap-8">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="saas-brand-link">
          <span className="w-8 h-8 rounded-md bg-gold grid place-items-center text-black font-black text-sm">
            {BRAND.logo.text.charAt(0)}
          </span>
          <div className="leading-tight">
            <div className="font-display font-extrabold tracking-tight text-white text-lg">{BRAND.logo.text}</div>
            {BRAND.badge ? (
              <div className="text-[9px] font-mono uppercase tracking-widest text-gold -mt-0.5">{BRAND.badge}</div>
            ) : null}
          </div>
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-6 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-white" : "text-white/60 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop right side */}
        <div className="hidden md:flex items-center gap-3 ml-auto">
          {/* Language */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => { setLangOpen((o) => !o); setAcctOpen(false); }}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs text-white/70 hover:text-white hover:bg-white/5 transition"
              data-testid="nav-language-btn"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-semibold">{activeLang?.code}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-44 p-1 rounded-lg bg-black/95 border border-white/10 shadow-2xl">
                {locales.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLocale(l.code); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md ${
                      l.code === locale ? "text-gold bg-white/5" : "text-white/80 hover:bg-white/5"
                    }`}
                    data-testid={`nav-lang-${l.code}`}
                  >
                    <span>{l.flag}</span>
                    <span className="flex-1 text-left">{l.label}</span>
                    {l.code === locale && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="relative" ref={acctRef}>
              <button
                onClick={() => { setAcctOpen((o) => !o); setLangOpen(false); }}
                className="inline-flex items-center gap-2 pl-2 pr-3 h-9 rounded-full border border-white/10 hover:border-white/25 hover:bg-white/5 transition"
                data-testid="nav-account-btn"
              >
                <span className="w-6 h-6 rounded-full bg-gold text-black grid place-items-center text-xs font-bold">
                  {(user.customer?.full_name || user.customer?.email || "?").charAt(0).toUpperCase()}
                </span>
                <span className="text-sm text-white/80 max-w-[120px] truncate">{user.customer?.full_name || user.customer?.email}</span>
                <ChevronDown className="w-3 h-3 text-white/60" />
              </button>
              {acctOpen && (
                <div className="absolute right-0 mt-2 w-56 p-1 rounded-lg bg-black/95 border border-white/10 shadow-2xl">
                  <div className="px-3 py-2 border-b border-white/10 mb-1">
                    <div className="text-sm text-white truncate">{user.customer?.full_name || "—"}</div>
                    <div className="text-[11px] text-white/50 truncate">{user.customer?.email}</div>
                  </div>
                  <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 rounded-md hover:bg-white/5">
                    <LayoutDashboard className="w-4 h-4" /> {t("nav.dashboard")}
                  </Link>
                  <Link to="/devices" className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 rounded-md hover:bg-white/5">
                    <User className="w-4 h-4" /> {t("nav.account")}
                  </Link>
                  <button
                    onClick={() => { logout(); navigate("/"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-err rounded-md hover:bg-err/10"
                    data-testid="nav-logout-btn"
                  >
                    <LogOut className="w-4 h-4" /> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-white/80 hover:text-white px-3 h-9 inline-flex items-center">
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-5 h-9 rounded-md bg-gold text-black text-sm font-bold hover:bg-gold-hover transition"
                data-testid="nav-register-btn"
              >
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen((o) => !o)}
          className="md:hidden ml-auto w-10 h-10 grid place-items-center rounded-md text-white/80 hover:bg-white/5"
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-5 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-md text-base ${
                    isActive ? "bg-white/10 text-white" : "text-white/80 hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3 mt-2 border-t border-white/10">
              {user ? (
                <>
                  <NavLink to="/dashboard" className="block px-3 py-2.5 rounded-md text-base text-white/80 hover:bg-white/5">
                    {t("nav.dashboard")}
                  </NavLink>
                  <NavLink to="/devices" className="block px-3 py-2.5 rounded-md text-base text-white/80 hover:bg-white/5">
                    {t("nav.account")}
                  </NavLink>
                  <button
                    onClick={() => { logout(); navigate("/"); }}
                    className="w-full text-left block px-3 py-2.5 rounded-md text-base text-err hover:bg-err/10"
                  >
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <div className="flex gap-3">
                  <NavLink to="/login" className="flex-1 h-11 rounded-md border border-white/15 grid place-items-center text-sm font-semibold text-white">
                    {t("nav.login")}
                  </NavLink>
                  <NavLink to="/register" className="flex-1 h-11 rounded-md bg-gold text-black grid place-items-center text-sm font-bold">
                    {t("nav.register")}
                  </NavLink>
                </div>
              )}
            </div>
            <div className="pt-4 mt-2 border-t border-white/10 flex flex-wrap gap-2">
              {locales.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLocale(l.code)}
                  className={`px-3 py-1.5 rounded-md text-sm ${
                    l.code === locale ? "bg-gold text-black" : "bg-white/5 text-white/80"
                  }`}
                >
                  <span className="mr-1.5">{l.flag}</span>{l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
